// src/platform/firebase/firestore/FirestoreServiceBase.ts
import { type QuerySpec } from '@/types/FirestoreQuery.ts'
import { type DocumentReference } from 'firebase/firestore'

export type WithId<T> = T & { id: string }

export interface IFirestoreService {
  // Path-based methods
  get<T>(path: string): Promise<WithId<T> | null>
  set<T>(path: string, data: T, merge?: boolean): Promise<void>
  update<T>(path: string, data: Partial<T>): Promise<void>
  remove(path: string): Promise<void>

  // Collection-based methods
  add<T>(collectionPath: string, data: T): Promise<string>
  getDoc<T>(collectionPath: string, id: string): Promise<WithId<T> | null>
  updateDoc<T>(collectionPath: string, id: string, data: Partial<T>): Promise<void>
  deleteDoc(collectionPath: string, id: string): Promise<void>
  getDocRef<T>(collectionPath: string, id: string): DocumentReference<T>

  runQuery<T>(spec: QuerySpec<T>): Promise<Array<WithId<T>>>
  runTransaction<T>(fn: (tx: unknown) => Promise<T>): Promise<T>
  count<T>(spec: QuerySpec<T>): Promise<number>
}

export abstract class FirestoreServiceBase implements IFirestoreService {
  // Path-based methods
  abstract get<T>(path: string): Promise<WithId<T> | null>
  abstract set<T>(path: string, data: T, merge?: boolean): Promise<void>
  abstract update<T>(path: string, data: Partial<T>): Promise<void>
  abstract remove(path: string): Promise<void>

  // Collection-based methods
  abstract add<T>(collectionPath: string, data: T): Promise<string>
  abstract getDoc<T>(collectionPath: string, id: string): Promise<WithId<T> | null>
  abstract updateDoc<T>(collectionPath: string, id: string, data: Partial<T>): Promise<void>
  abstract deleteDoc(collectionPath: string, id: string): Promise<void>
  abstract getDocRef<T>(collectionPath: string, id: string): DocumentReference<T>

  abstract runQuery<T>(spec: QuerySpec<T>): Promise<Array<WithId<T>>>
  abstract runTransaction<T>(fn: (tx: unknown) => Promise<T>): Promise<T>
  abstract count<T>(spec: QuerySpec<T>): Promise<number>
}
